import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/** Settings → Productivitee (settings/Productivitee::*): etichetarea aplicatiilor pe grup si pe firma. */
export type ProductiviteeSettingsTab = { id: string; label: string; icon: string; href: string; right: string }
export type ProductiviteeSettingsShell = { pageTitle: string; moduleUrl: string; tabs: ProductiviteeSettingsTab[] }

export const TABS = ['application_tagging', 'master_tagging', 'general'] as const
export type ProductiviteeSettingsTabId = (typeof TABS)[number]

export function isProductiviteeSettingsTab(value: string): value is ProductiviteeSettingsTabId {
  return (TABS as readonly string[]).includes(value)
}

/* --------------------------------------------- General settings (pe firma) */

/**
 * Settings → Productivitee → General settings: raportul de seara catre oameni
 * si metoda implicita de calcul a firmei.
 *
 * `days` sunt ID-uri din `it_productivitee_default_business_days` (1 luni ..
 * 7 duminica), aceeasi numerotare cu programul masinilor. `weekdays` vine de
 * la server cu numele lor, ca ecranul sa nu-si scrie a doua oara zilele
 * saptamanii.
 */
export type ProductiviteeDayReport = {
  enabled: boolean
  hour: string
  days: number[]
  userMayChangeHour: boolean
  userMayChangeSchedule: boolean
}

export type ProductiviteeGeneral = {
  method: number
  dayReport: ProductiviteeDayReport
  sections: string[]
  methods: { ID: number; method_name: string }[]
  weekdays: { id: number; name: string }[]
}

/** Raspunsul unei scrieri: setarile RECITITE de pe server, plus randul de confirmare. */
export type ProductiviteeGeneralAnswer = ProductiviteeGeneral & { ok?: boolean; message?: string }

export const GENERAL_KEY = ['settings', 'productivitee', 'general'] as const

export function productiviteeGeneralQuery() {
  return queryOptions({ queryKey: GENERAL_KEY, queryFn: () => apiGet<ProductiviteeGeneral>('/settings/productivitee/general') })
}

/**
 * Ce se poate trimite intr-o scriere. Se trimit NUMAI campurile date: ecranul
 * are doua formulare, si fiecare il salveaza pe al lui fara sa-l calce pe
 * celalalt.
 */
export type ProductiviteeGeneralWrite = Partial<{
  enabled: boolean
  hour: string
  days: number[]
  user_hour: boolean
  user_schedule: boolean
  method: number
}>

/**
 * Scrierea primeste datele PE FATA, nu dintr-o inchidere peste starea
 * ecranului: `mutate(payload)`, nu `mutate()`. O inchidere veche a mai sters
 * randuri pe ecranele astea.
 */
export function saveProductiviteeGeneral(write: ProductiviteeGeneralWrite) {
  const fields: Record<string, PostValue> = {}
  if (write.enabled !== undefined) fields.enabled = write.enabled ? 1 : 0
  if (write.hour !== undefined) fields.hour = write.hour
  if (write.days !== undefined) fields.days = write.days.join(',')
  if (write.user_hour !== undefined) fields.user_hour = write.user_hour ? 1 : 0
  if (write.user_schedule !== undefined) fields.user_schedule = write.user_schedule ? 1 : 0
  if (write.method !== undefined) fields.method = write.method
  return apiPost<ProductiviteeGeneralAnswer>('/settings/productivitee/general', fields)
}

/** Un grup al firmei, cu pragul lui de inactivitate (idle) in secunde (implicit 300 = 5 min). */
export type ProductiviteeGroup = { ID: number; name: string; inactivitySeconds: number }
export type GroupsPage = { company: number; groups: ProductiviteeGroup[] }
/** Raspunsul scrierii pragului: lista de grupuri RECITITA de pe server, plus randul de confirmare. */
export type GroupInactivityAnswer = { ok?: boolean; message?: string; groups?: ProductiviteeGroup[] }
export type GroupAppRow = { DT_RowId: string; ID: number; application_name: string; application_type: string; group_name: string; GroupID: number; tag_type: number }
export type CompanyAppRow = { DT_RowId: string; ID: number; application_name: string; application_type: string; tag_type: number }
export type WriteAnswer = { ok?: boolean; message?: string; groups?: number }

export function productiviteeSettingsShellQuery() {
  return queryOptions({ queryKey: ['settings', 'productivitee', 'shell'], queryFn: () => apiGet<ProductiviteeSettingsShell>('/settings/productivitee') })
}

export function groupsQuery() {
  return queryOptions({ queryKey: ['settings', 'productivitee', 'groups'], queryFn: () => apiGet<GroupsPage>('/settings/productivitee/groups') })
}

/** Pagina ceruta de DataTable (serverSide): `order[0][column]` e indicele din `GROUP_COLUMNS` / `COMPANY_COLUMNS` ale sursei. */
export type TaggingRequest = { start: number; length: number; orderColumn: number; orderDir: 'asc' | 'desc'; search: string }
export type TaggingPage<Row> = { data: Row[]; recordsTotal: number; recordsFiltered?: number }

function tableFields(r: TaggingRequest): Record<string, PostValue> {
  return { draw: 1, start: r.start, length: r.length, 'order[0][column]': r.orderColumn, 'order[0][dir]': r.orderDir, 'search[value]': r.search }
}

export function groupTableQuery(group: number, r: TaggingRequest) {
  return queryOptions({
    queryKey: ['settings', 'productivitee', 'tagging', group, r],
    queryFn: () => apiPost<TaggingPage<GroupAppRow>>('/settings/productivitee/tagging/table', { selectedGroup: group, ...tableFields(r) }),
    enabled: group > 0,
  })
}

export function companyTableQuery(r: TaggingRequest) {
  return queryOptions({
    queryKey: ['settings', 'productivitee', 'master', r],
    queryFn: () => apiPost<TaggingPage<CompanyAppRow>>('/settings/productivitee/master/table', tableFields(r)),
  })
}

export function post(path: string, fields: Record<string, PostValue> = {}) {
  return apiPost<WriteAnswer>('/settings/productivitee' + path, fields)
}

/**
 * Scrie pragul de inactivitate (idle) al unui grup. Se afiseaza si se editeaza
 * in MINUTE pe ecran, dar spre server pleaca in SECUNDE - contractul (si
 * serverul ph4) sunt in secunde. Raspunsul poarta lista de grupuri recitita.
 */
export function saveGroupInactivity(group: number, seconds: number) {
  return apiPost<GroupInactivityAnswer>('/settings/productivitee/groups/inactivity', { group, seconds })
}
